/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import PropTypes from 'prop-types';
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import Tabs from '../../ui/Tabs';
import { Container, Row, Column } from '../../../styles/Grid';
import Spacer from '../../ui/Spacer';
import DotComponent from '../../ui/DotComponent';
import { composeStringToId } from '../../../utils/stringToId';
import TabContent from './TabContent';
import { withNamespaces } from 'react-i18next';
import LottieAnimation, {
  getTopicsAnimationIdFromIndex,
} from '../../ui/LottieAnimation';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import {
  containerPaddingCss,
  containerMarginCss,
  headlineCss,
  itemTabCss,
  textHeaderCss,
  TabCtaCss,
  animationContainerCss,
  animationContentCss,
  solutionTabContentCss,
  separatorLineCss,
} from './index.styles';

export const defaultData = {
  BackgroundColor: {
    fields: {
      Value: {
        value: 'transparent',
      },
    },
  },
  ModuleHeadline: {
    value: '',
  },
  items: [
    {
      tabName: { value: '' },
      imageSrc: '',
      label: {
        value: 'office_space',
      },
      content: {
        value: {
          Headline: { value: '' },
          Description: { value: '' },
          itemList: [],
          CTA: {
            value: {
              href: '',
              text: '',
            },
          },
        },
      },
    },
    {
      tabName: { value: '' },
      imageSrc: '',
      label: {
        value: 'co_working_space',
      },
      content: {
        value: {
          Headline: { value: '' },
          Description: { value: '' },
          itemList: [],
          CTA: {
            value: {
              href: '',
              text: '',
            },
          },
        },
      },
    },
  ],
};

const TabHeader = ({ name, label, onChangeTabs, active }) => (
  <li css={itemTabCss} key={composeStringToId(label, 'solution-tabs')}>
    <button onClick={onChangeTabs} id={label} css={TabCtaCss}>
      <span data-active={active} css={textHeaderCss}>
        <Text field={name} />
      </span>
    </button>
  </li>
);

export class SolutionTabs extends SafePureComponent {
  state = {
    currentAnimationId: null,
  };

  componentDidMount() {
    this.setState({ currentAnimationId: getTopicsAnimationIdFromIndex(0) });
  }

  changeAnimation = index => {
    const nextAnimationId = getTopicsAnimationIdFromIndex(index);
    this.setState({ currentAnimationId: nextAnimationId });
  };

  renderHeading(fields) {
    return (
      <Container>
        <Row>
          <Column cols={{ sm: 6, md: 8, lg: 7 }} offsets={{ lg: 1 }}>
            <h2 css={headlineCss}>
              <Text field={fields.ModuleHeadline} />
              <DotComponent />
            </h2>
          </Column>
        </Row>
      </Container>
    );
  }

  renderLottieAnimation() {
    const { currentAnimationId } = this.state;

    return (
      <div css={animationContainerCss}>
        <Container>
          <Row>
            <Column cols={{ sm: 6, md: 4, lg: 5 }}>
              <div css={animationContentCss}>
                <LottieAnimation
                  height={300}
                  nextAnimationId={currentAnimationId}
                  preserveAspectRatio="xMidYMin meet"
                />
              </div>
            </Column>
          </Row>
        </Container>
      </div>
    );
  }

  safeRender() {
    const { props } = this;
    const fields = defaultsDeep(props.fields, defaultData);
    const firstItemActive = get(fields, 'items[0].label.value', '');
    return (
      <Spacer
        backgroundColor={fields.BackgroundColor}
        paddingCss={containerPaddingCss}
        marginCss={containerMarginCss}
      >
        {this.renderHeading(fields)}
        {this.renderLottieAnimation()}
        <Container>
          <Row css={separatorLineCss}>
            <Column cols={{ sm: 6, md: 8, lg: 12 }}>
              <Tabs
                showLeftEffect
                firstItemActive={firstItemActive}
                renderTabs={(activeTab, onChangeTabs) =>
                  fields.items.map((item, index) => {
                    const labelValue = get(item, 'label.value', '');
                    return (
                      <TabHeader
                        active={labelValue === activeTab}
                        onChangeTabs={(...args) => {
                          this.changeAnimation(index);
                          onChangeTabs(...args);
                        }}
                        key={composeStringToId(labelValue, 'tab-header')}
                        name={item.tabName}
                        label={labelValue}
                      />
                    );
                  })
                }
                renderContent={activeTab =>
                  fields.items.map(item => {
                    const labelValue = get(item, 'label.value', '');
                    return (
                      <TabContent
                        css={solutionTabContentCss}
                        active={labelValue === activeTab}
                        key={composeStringToId(labelValue, 'tab-content')}
                        content={item.content}
                        label={labelValue}
                        tabName={item.tabName}
                        t={props.t}
                      />
                    );
                  })
                }
              />
            </Column>
          </Row>
        </Container>
      </Spacer>
    );
  }
}

SolutionTabs.defaultProps = {
  t: key => key,
};

SolutionTabs.propTypes = {
  BackgroundColor: PropTypes.shape({
    fields: PropTypes.shape({
      Value: PropTypes.shape({
        value: PropTypes.string,
      }),
    }),
  }),
  ModuleHeadline: PropTypes.shape({
    value: PropTypes.string,
  }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      tabName: PropTypes.shape({
        value: PropTypes.string.isRequired,
      }),
      label: PropTypes.shape({
        value: PropTypes.string.isRequired,
      }),
      content: PropTypes.shape({
        value: PropTypes.object.isRequired,
      }),
    }),
  ),
  t: PropTypes.func,
};

SolutionTabs.displayName = 'SolutionTabs';

export default withNamespaces()(SolutionTabs);
